export const SUPPORT_EMAIL = 'support@turbamedia.io'

const SUBJECT = 'Turba Media Support'

interface LocationLike {
    pathname: string
    params: Record<string, string | undefined>
    search?: string
}

/**
 * The license (campaign) the user is currently looking at, or null. License pages carry the id as the `:id`
 * route param under /campaigns, and the Subscriptions page carries the selected license in `?license=`.
 */
export const licenseIdFromLocation = ({ pathname, params, search = '' }: LocationLike): string | null => {
    if (pathname.startsWith('/campaigns/') && params.id) return params.id
    if (pathname === '/subscriptions') return new URLSearchParams(search).get('license') || null
    return null
}

export interface LicenseLike {
    id: string
    name?: string | null
    hasSubscriptions?: boolean | null
    systemStatus?: string | null
}

/**
 * The one license to name when the page is not about a license: a subscribed one (an active one first), or the
 * first of the user's licenses when none is subscribed yet.
 */
export const fallbackLicense = (licenses: LicenseLike[]): LicenseLike | null => {
    const subscribed = licenses.filter((license) => license.hasSubscriptions)
    const pool = subscribed.length ? subscribed : licenses
    return pool.find((license) => license.systemStatus === 'ACTIVE') ?? pool[0] ?? null
}

/**
 * The license to name in the support email: the page's license (with its name when the user's list has it),
 * otherwise the fallback from the list.
 */
export const supportLicense = (pageLicenseId: string | null, licenses: LicenseLike[]): LicenseLike | null => {
    if (pageLicenseId) return licenses.find((license) => license.id === pageLicenseId) ?? { id: pageLicenseId }
    return fallbackLicense(licenses)
}

/** "Summer Sale (ID: lic-42)" when the name is known, otherwise "ID: lic-42" */
const describeLicense = ({ id, name }: LicenseLike): string => (name ? `${name} (ID: ${id})` : `ID: ${id}`)

/** Names the license by name only; the id stays out of the subject and goes in the body */
export const supportSubject = (license: LicenseLike | null | undefined): string =>
    license?.name ? `${SUBJECT} - License ${license.name}` : SUBJECT

/** A short starter body: the license, room for the problem, and a reminder to attach a screenshot */
export const supportBody = (license: LicenseLike | null | undefined): string =>
    [
        'Hi Turba Media Support,',
        '',
        ...(license ? [`License: ${describeLicense(license)}`, ''] : []),
        'Describe your problem here:',
        '',
        '',
        'Please attach a screenshot of the problem.',
        '',
        'Thanks,',
        '',
    ].join('\r\n')

/** A mailto: link to support whose subject and body name the license, when one is known */
export const supportMailto = (license: LicenseLike | null | undefined): string => {
    const subject = encodeURIComponent(supportSubject(license))
    const body = encodeURIComponent(supportBody(license))
    return `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`
}
